<template>
  <div
    ref="scrollContainer"
    class="flex-1"
    :class="{ 'overflow-auto': !scrollProxy }"
  >
    <div class="timeline-container relative pb-4">
      <!-- 时间线垂直线 -->
      <div
        v-if="timelineEvents.length > 0"
        class="absolute left-4 top-0 bottom-0 w-[1px] bg-base-content/30"
      ></div>

      <!-- 时间线事件列表 -->
      <div :class="['flex flex-col', mode === 'sort' ? 'gap-1 pt-5' : 'gap-4']">
        <div v-if="timelineEvents.length === 0">
          <VanEmpty
            image-size="100"
            :description="$t(`module.timeline.empty`)"
          />

          <div class="mt-4 flex justify-center" v-if="mode === 'edit'">
            <button class="btn btn-primary" @click="clickAddEvent(undefined)">
              + {{ $t(`Buttons.event`) }}
            </button>
          </div>
        </div>

        <!-- 添加事件按钮 -->
        <div
          class="mt-4 ml-8 mr-3"
          v-else-if="mode === 'edit' && !eventLoading"
        >
          <button class="btn btn-xs w-full" @click="clickAddEvent(undefined)">
            + {{ $t(`Buttons.event`) }}
          </button>
        </div>

        <div v-if="eventLoading" class="flex justify-center items-center pt-4">
          <div class="loading loading-ring loading-lg" />
        </div>
        <div
          v-else
          v-for="(event, i) in timelineEvents"
          :key="event.id"
          :data-index="i"
          :draggable="isEditMode"
          :class="[
            dragTargetIndex === i && 'border-2 border-primary',
            dragIndex === i && 'bg-primary/50 ',
          ]"
          @dragstart="dragStart($event, i)"
          @dragenter="dragEnter($event, i)"
          @dragend="dragEnd"
          @dragover="dragOver"
          @touchstart="touchStart($event, i, timelineEvents.length)"
          @touchmove="touchMove"
          @touchend="touchEnd"
          class="timeline-event relative pl-8 pr-3"
        >
          <!-- 时间线圆点 -->
          <div
            class="absolute left-[12px] top-2 w-2.5 h-2.5 bg-primary rounded-full"
          ></div>

          <!-- 事件内容 -->
          <div class="rounded-lg mb-2">
            <!-- 事件ID -->
            <div
              class="flex justify-between items-center mt-3"
              v-if="mode === 'sort'"
            >
              <div class="text-sm flex items-center gap-1">
                {{ $t(`module.timeline.sort`) }}:{{ event.order }}
                <button
                  class="btn btn-xs"
                  @click.stop="clickEditEvent(i, 'order')"
                >
                  <i class="iconify mingcute--edit-3-line" />
                </button>
              </div>
            </div>
            <div class="flex items-center gap-1">
              {{ event.time }}
              <button
                v-if="mode === 'edit'"
                class="btn btn-xs"
                @click="clickEditEvent(i, 'time')"
              >
                <i class="iconify mingcute--edit-3-line text-xs"></i>
              </button>
            </div>
            <div
              v-if="mode !== 'sort'"
              class="my-2 prose prose-sm same-size"
              v-html="processHtmlImageSrc(event.content)"
            />

            <!-- 事件关联实体 -->
            <div
              class="flex gap-2"
              :class="{ 'overflow-x-auto': !disableXScroll }"
            >
              <template v-for="item in event.itemzz" :key="item.id">
                <div v-if="item.entity" class="flex-shrink-0 relative">
                  <button
                    v-if="mode === 'edit'"
                    class="absolute z-[1] top-0 right-0 cursor-pointer"
                    @click="clickRemoveRelation(event, item)"
                  >
                    <i class="iconify mingcute--close-circle-fill"></i>
                  </button>

                  <TimelineModuleEntityCard :mode="mode" :item="item" />
                </div>
              </template>
            </div>
          </div>

          <!-- 事件操作 -->
          <div>
            <div
              v-if="mode === 'edit'"
              class="flex items-center gap-1 justify-end"
            >
              <button
                class="btn btn-xs btn-outline btn-primary"
                @click="clickMateWish(event)"
              >
                {{ $t(`mateWish.name`) }}
              </button>
              <button
                class="btn btn-xs flex items-center gap-1"
                @click="clickAddRelation(event)"
              >
                {{ $t(`Buttons.relation`) }}
              </button>
              <button
                class="btn btn-xs flex items-center gap-1"
                @click="clickEditEvent(i, 'content')"
              >
                <i class="iconify mingcute--edit-3-line" />
                {{ $t(`Buttons.editEvent`) }}
              </button>
              <button class="btn btn-xs" @click="clickDeleteEvent(event.id, i)">
                <i class="iconify mingcute--delete-2-line" />
              </button>
            </div>
          </div>

          <!-- 添加事件按钮 -->
          <div class="mt-4" v-if="mode === 'edit'">
            <button
              class="btn btn-xs w-full"
              @click="clickAddEvent(event.order)"
            >
              + {{ $t(`Buttons.event`) }}
            </button>
          </div>
        </div>
        <ModalSelectEntity
          v-model:show="showSelectEntity"
          :exclude-mate-ids="excludeMateIds"
          :exclude-storybook-ids="excludeStorybookIds"
          :exclude-moment-ids="excludeMomentIds"
          @selected="onSelectedEntity"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  createOneTimelineEvent,
  createOneTimelineEventItem,
  deleteOneTimelineEvent,
  deleteOneTimelineEventItem,
  updateManyTimelineEvent,
  updateOneTimelineEvent,
} from "@/apis/user";
import { AIWriteType } from "@/enums/mateEnums";
import { processHtmlImageSrc } from "@/modules/editor/editor-json-utils";
import { useGlobalModal } from "@/stores/globalModal";
import { useModule } from "@/stores/module";
import { sessionStorageSet } from "@/tools/sessionStorage";
import {
  Mate,
  MateWish,
  SelectEntityType,
  StoryBook,
  TimelineEvent,
  TimelineEventItem,
} from "@/types/entity";
import { useDrag } from "@/use/drag";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { computed, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { removeHtmlTag } from "@/tools/text";
import { isMobile } from "@/modules/common";

const props = defineProps({
  mode: {
    type: String as PropType<"edit" | "sort" | "preview">,
    default: "edit",
  },
  timelineEvents: {
    type: Array as PropType<TimelineEvent[]>,
    default: () => [],
  },
  eventLoading: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: "reload"): void;
  (e: "update:isSaving", value: boolean): void;
}>();

// const showAddEvent = ref(false);
const event = ref<TimelineEvent>({} as TimelineEvent);
const moduleStore = useModule();

const modalStore = useGlobalModal();
const { t } = useI18n();
const router = useRouter();

const scrollContainer = ref<HTMLElement>();
const route = useRoute();

/**
 * 是否禁用X轴滚动
 * 若组件处于{@linkcode BottomSheetLayout} 组件中的时候，需要禁用X滚动
 * 因为X滚动和整体的Y滚动冲突，当对X滚动的元素进行滚动的时候，整体的垂直滚动会失效
 * 导致列表滚动体验比较差，所以在这个组件中禁用。
 */
const disableXScroll = computed(() => {
  return route.name === "interactive" && isMobile();
});
const scrollProxy = computed(() => {
  return route.name === "interactive";
});

const clickAddEvent = async (order?: number) => {
  event.value = {} as TimelineEvent;
  const { timelineEvents } = props;
  // order++;

  //
  let itemIndex = -1;
  if (typeof order === "number") {
    itemIndex = timelineEvents.findIndex((v) => v.order === order + 1);
    // 如果排序ID已存在，则提示用户确认
    if (itemIndex !== -1) {
      try {
        await showConfirmDialog({
          message: t(`Message.timelineOrderDuplicate`, { order: order + 1 }),
          confirmButtonText: t(`Buttons.confirm`),
          cancelButtonText: t(`Buttons.cancel`),
        });
      } catch (e) {
        return;
      }
    }
  }

  // 打开输入框
  modalStore.openTextInputModal({
    title: t(`module.timeline.time`),
    callback: async (value) => {
      event.value.time = value!;

      const loading = showLoadingToast(t(`Message.creating`));

      try {
        emit("update:isSaving", true);
        const { item } = await createOneTimelineEvent({
          entityId: moduleStore.module!.entity.id!,
          entityType: moduleStore.module!.entityType,
          time: value,
          order,
        });
        if (itemIndex !== -1) {
          for (let i = itemIndex; i < timelineEvents.length; i++) {
            timelineEvents[i].order++;
          }
        }

        if (typeof order !== "number") {
          itemIndex = 0;
        } else if (itemIndex === -1) {
          itemIndex = timelineEvents.findIndex((v) => v.order === order);
          itemIndex++;
        }

        timelineEvents.splice(itemIndex, 0, item);

        loading.close();
        showSuccessToast(t(`Message.createdSuccessfully`));
      } catch (e) {
        loading.close();
      } finally {
        emit("update:isSaving", false);
      }
    },
  });
};

const clickEditEvent = (index: number, field: "time" | "content" | "order") => {
  event.value = props.timelineEvents[index];
  if (field === "time") {
    modalStore.openTextInputModal({
      title: t(`module.timeline.time`),
      initValue: event.value.time,
      callback: async (value) => {
        await updateTimelineEvent({
          time: value,
        });
        event.value.time = value!;
      },
    });
  } else if (field === "content") {
    modalStore.openRichEditor({
      title: event.value.time,
      content: event.value.content,
      moduleName: t(`SidebarNavi.timeline`),
      aiType: AIWriteType.Module,
      options: {
        entityId: moduleStore.module!.entity.id!,
        entityType: moduleStore.module!.entityType,
      },
      callback: async (value) => {
        await updateTimelineEvent({
          content: value,
        });
        event.value.content = value!;
      },
    });
  } else if (field === "order") {
    modalStore.openTextInputModal({
      title: t(`module.timeline.order`),
      callback: async (value) => {
        try {
          await updateTimelineEvent({
            order: parseInt(value!),
          });
          event.value.order = parseInt(value!);
          const timelineEvents = props.timelineEvents;
          timelineEvents.sort((a, b) => a.order - b.order);
        } catch (e) {
          console.log(e);
        }
      },
    });
  }
};

const clickDeleteEvent = async (id: number, index: number) => {
  try {
    await showConfirmDialog({
      title: t(`Message.deleteConfirm`),
      confirmButtonText: t(`Buttons.confirm`),
      cancelButtonText: t(`Buttons.cancel`),
    });
  } catch (e) {
    return;
  }
  try {
    emit("update:isSaving", true);
    await deleteOneTimelineEvent(id);
    const timelineEvents = props.timelineEvents;
    timelineEvents.splice(index, 1);
  } finally {
    emit("update:isSaving", false);
  }
};

const updateTimelineEvent = async (params: Partial<TimelineEvent>) => {
  try {
    emit("update:isSaving", true);
    await updateOneTimelineEvent(event.value.id, params);
  } finally {
    emit("update:isSaving", false);
  }
};

const showSelectEntity = ref(false);
const excludeMateIds = ref<number[]>([]);
const excludeStorybookIds = ref<number[]>([]);
const excludeMomentIds = ref<number[]>([]);
const activeTimelineEvent = ref<TimelineEvent>();
const clickAddRelation = async (event: TimelineEvent) => {
  excludeMateIds.value = [];
  excludeStorybookIds.value = [];
  excludeMomentIds.value = [];
  activeTimelineEvent.value = event;
  event.itemzz.forEach((item) => {
    if (item.entityType === "mate") {
      excludeMateIds.value.push(item.entityId);
    } else if (item.entityType === "storyBook") {
      excludeStorybookIds.value.push(item.entityId);
    } else if (item.entityType === "moment") {
      excludeMomentIds.value.push(item.entityId);
    }
  });
  showSelectEntity.value = true;
};

const clickRemoveRelation = async (
  event: TimelineEvent,
  item: TimelineEventItem
) => {
  try {
    await showConfirmDialog({
      message: t(`Message.confirmRemoveRelation`),
      confirmButtonText: t(`Buttons.confirm`),
      cancelButtonText: t(`Buttons.cancel`),
    });
  } catch (e) {
    return;
  }
  const loading = showLoadingToast(t(`Message.deleting`));
  await deleteOneTimelineEventItem(item.id);
  sessionStorageSet(`reload-timeline`, true);
  event.itemzz = event.itemzz.filter((v) => v.id !== item.id);
  loading.close();
};

const onSelectedEntity = async (
  data: number[] | Mate | StoryBook,
  entityType: SelectEntityType
) => {
  const loading = showLoadingToast(t(`Message.creating`));
  if (entityType === "moment" && Array.isArray(data) && data.length > 1) {
    // 多个moment
    await Promise.all(
      data.map((v) =>
        createOneTimelineEventItem({
          eventId: activeTimelineEvent.value!.id,
          entityType,
          entityId: v,
        })
      )
    );
  } else {
    let entityId;
    if (Array.isArray(data)) {
      entityId = data[0];
    } else {
      entityId = data.id!;
    }
    await createOneTimelineEventItem({
      eventId: activeTimelineEvent.value!.id,
      entityType,
      entityId,
    });
  }

  sessionStorageSet(`reload-timeline`, true);
  emit("reload");
  loading.close();

  showSelectEntity.value = false;
};

const clickMateWish = (event: TimelineEvent) => {
  let { content } = event;
  content = removeHtmlTag(content);
  let params: Partial<MateWish> = {
    content,
    characterIdzz: [],
  };
  if (moduleStore.module?.entityType === "mate")
    params.characterIdzz!.push(moduleStore.module.entity.id!);

  sessionStorageSet("mate-wish-edit", params);
  router.push({ name: `mateWishEdit` });
};

const isEditMode = computed<boolean>(() => props.mode === "sort");
const {
  dragIndex,
  dragTargetIndex,
  dragStart,
  dragEnd,
  dragEnter,
  dragOver,
  touchStart,
  touchMove,
  touchEnd,
} = useDrag(
  {
    isDraggingMode: isEditMode,
    scrollContainer: scrollContainer,
  },
  async (originIndex, targetIndex) => {
    if (originIndex === targetIndex) return;
    console.log(originIndex, targetIndex);
    let originOrder = props.timelineEvents[originIndex].order;
    let targetOrder = props.timelineEvents[targetIndex].order;
    let origin = props.timelineEvents[originIndex];
    let target = props.timelineEvents[targetIndex];
    emit("update:isSaving", true);
    await updateManyTimelineEvent([
      {
        id: origin.id,
        order: targetOrder,
      },
      {
        id: target.id,
        order: originOrder,
      },
    ]);

    origin.order = targetOrder;
    target.order = originOrder;
    const timelineEvents = props.timelineEvents;
    timelineEvents.sort((a, b) => a.order - b.order);
    emit("update:isSaving", false);
  }
);
</script>
